import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  ArrowLeft, 
  Copy, 
  Share2, 
  QrCode,
  Link2,
  Check,
  X,
  Clock,
  CheckCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import QRCode from "react-qr-code";

const Receive = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("request");
  const [requestData, setRequestData] = useState({
    amount: "",
    currency: "JMD",
    note: "",
    payer_hint: ""
  });
  const [linkData, setLinkData] = useState({
    amount: "",
    currency: "JMD",
    note: "",
    amount_locked: false
  });
  const [qrAmount, setQrAmount] = useState("");
  const [qrNote, setQrNote] = useState("");
  const [showSuccess, setShowSuccess] = useState(null);

  // Fetch existing requests
  const { data: requests } = useQuery({
    queryKey: ["paymentRequests"],
    queryFn: async () => {
      const response = await axios.get(`${API}/receive/requests`);
      return response.data;
    }
  });

  // Fetch QR payload
  const { data: qrPayload, refetch: refetchQR } = useQuery({
    queryKey: ["qrPayload", qrAmount, qrNote],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (qrAmount) params.append("amount", qrAmount);
      params.append("currency", "JMD");
      if (qrNote) params.append("note", qrNote);
      const response = await axios.get(`${API}/receive/qr-payload?${params}`);
      return response.data;
    }
  });

  // Create request mutation
  const createRequestMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/receive/request`, {
        amount: requestData.amount ? parseFloat(requestData.amount) : null,
        currency: requestData.currency,
        note: requestData.note || null,
        payer_hint: requestData.payer_hint || null
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["paymentRequests"]);
      setShowSuccess({ type: "request", data });
      toast.success("Request created!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to create request");
    }
  });

  // Create link mutation
  const createLinkMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/receive/link`, {
        amount: linkData.amount ? parseFloat(linkData.amount) : null,
        currency: linkData.currency,
        note: linkData.note || null,
        amount_locked: linkData.amount_locked
      });
      return response.data;
    },
    onSuccess: (data) => {
      setShowSuccess({ type: "link", data });
      toast.success("Payment link created!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to create link");
    }
  });

  // Cancel request mutation
  const cancelMutation = useMutation({
    mutationFn: async (requestId) => {
      await axios.post(`${API}/receive/request/${requestId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentRequests"]);
      toast.success("Request canceled");
    }
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleShare = async (data) => {
    const shareData = {
      title: "Stelx Payment Request",
      text: data.amount 
        ? `Pay J$${data.amount} to ${user?.name}` 
        : `Send payment to ${user?.name}`,
      url: `https://pay.stelxpay.com/${data.link_token}`
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        copyToClipboard(`https://pay.stelxpay.com/${data.link_token}`);
      }
    } else {
      copyToClipboard(`https://pay.stelxpay.com/${data.link_token}`);
    }
  };

  const formatCurrency = (amount) => {
    return `J$${parseFloat(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "PAID": return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case "EXPIRED": return <XCircle className="w-4 h-4 text-rose-400" />;
      case "CANCELED": return <XCircle className="w-4 h-4 text-muted-foreground" />;
      default: return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="receive-page">
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="text-foreground"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Receive Money</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-secondary/50 mb-6">
            <TabsTrigger value="request" data-testid="tab-request">Request</TabsTrigger>
            <TabsTrigger value="qr" data-testid="tab-qr">QR Code</TabsTrigger>
            <TabsTrigger value="link" data-testid="tab-link">Link</TabsTrigger>
          </TabsList>

          {/* REQUEST TAB */}
          <TabsContent value="request" className="space-y-6">
            <div className="glass rounded-3xl p-6">
              <h2 className="font-heading font-medium text-lg mb-4">Create Request</h2>
              <div className="space-y-4">
                <div>
                  <Label>Amount (optional)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="input-glass pl-10 font-mono"
                      value={requestData.amount}
                      onChange={(e) => setRequestData({ ...requestData, amount: e.target.value })}
                      data-testid="request-amount"
                    />
                  </div>
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Input
                    placeholder="What's this for?"
                    className="input-glass mt-2"
                    value={requestData.note}
                    onChange={(e) => setRequestData({ ...requestData, note: e.target.value })}
                    data-testid="request-note"
                  />
                </div>
                <div>
                  <Label>Request from (optional)</Label>
                  <Input
                    placeholder="Email, phone, or username"
                    className="input-glass mt-2"
                    value={requestData.payer_hint}
                    onChange={(e) => setRequestData({ ...requestData, payer_hint: e.target.value })}
                    data-testid="request-payer"
                  />
                </div>
                <Button
                  className="w-full btn-primary h-12"
                  onClick={() => createRequestMutation.mutate()}
                  disabled={createRequestMutation.isPending}
                  data-testid="create-request-btn"
                >
                  {createRequestMutation.isPending ? "Creating..." : "Create Request"}
                </Button>
              </div>
            </div>

            {/* Existing Requests */}
            {requests && requests.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-3">
                  Recent Requests
                </p>
                <div className="space-y-2">
                  {requests.slice(0, 5).map((req) => (
                    <div key={req.request_id} className="glass rounded-2xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(req.status)}
                        <div>
                          <p className="text-sm font-medium">
                            {req.amount ? formatCurrency(req.amount) : "Open amount"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {req.note || "No note"} • {req.status}
                          </p>
                        </div>
                      </div>
                      {req.status === "PENDING" && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleShare(req)}
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-400"
                            onClick={() => cancelMutation.mutate(req.request_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* QR CODE TAB */}
          <TabsContent value="qr" className="space-y-6">
            <div className="glass rounded-3xl p-6 text-center">
              <h2 className="font-heading font-medium text-lg mb-4">Your Payment QR</h2>
              
              {qrPayload && (
                <div className="bg-white p-4 rounded-2xl inline-block mb-4">
                  <QRCode
                    value={qrPayload.payload}
                    size={200}
                    level="H"
                  />
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mb-4">
                {user?.name} • {qrAmount ? `J$${qrAmount}` : "Any amount"}
              </p>
              
              <div className="flex gap-2 justify-center">
                <Button
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => copyToClipboard(qrPayload?.payload || "")}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
                <Button
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => refetchQR()}
                >
                  Refresh
                </Button>
              </div>
            </div>

            <div className="glass rounded-2xl p-4 space-y-4">
              <div>
                <Label>Set Amount (optional)</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  className="input-glass mt-2"
                  value={qrAmount}
                  onChange={(e) => setQrAmount(e.target.value)}
                  data-testid="qr-amount"
                />
              </div>
              <div>
                <Label>Add Note (optional)</Label>
                <Input
                  placeholder="Payment description"
                  className="input-glass mt-2"
                  value={qrNote}
                  onChange={(e) => setQrNote(e.target.value)}
                  data-testid="qr-note"
                />
              </div>
            </div>
          </TabsContent>

          {/* LINK TAB */}
          <TabsContent value="link" className="space-y-6">
            <div className="glass rounded-3xl p-6">
              <h2 className="font-heading font-medium text-lg mb-4">Create Payment Link</h2>
              <div className="space-y-4">
                <div>
                  <Label>Amount (optional)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="input-glass pl-10 font-mono"
                      value={linkData.amount}
                      onChange={(e) => setLinkData({ ...linkData, amount: e.target.value })}
                      data-testid="link-amount"
                    />
                  </div>
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Input
                    placeholder="What's this for?"
                    className="input-glass mt-2"
                    value={linkData.note}
                    onChange={(e) => setLinkData({ ...linkData, note: e.target.value })}
                    data-testid="link-note"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium">Lock amount</p>
                    <p className="text-xs text-muted-foreground">Payer cannot change amount</p>
                  </div>
                  <button
                    onClick={() => setLinkData({ ...linkData, amount_locked: !linkData.amount_locked })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      linkData.amount_locked ? "bg-primary" : "bg-secondary"
                    }`}
                    data-testid="lock-amount-toggle"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      linkData.amount_locked ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </div>
                <Button
                  className="w-full btn-primary h-12"
                  onClick={() => createLinkMutation.mutate()}
                  disabled={createLinkMutation.isPending}
                  data-testid="create-link-btn"
                >
                  <Link2 className="w-5 h-5 mr-2" />
                  {createLinkMutation.isPending ? "Creating..." : "Create Link"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Success Modal */}
      {showSuccess && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowSuccess(null)}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2">
                {showSuccess.type === "request" ? "Request Created!" : "Link Created!"}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Share this with the person you want to receive payment from
              </p>
              
              <div className="glass rounded-xl p-3 mb-4 text-left">
                <p className="text-xs text-muted-foreground mb-1">Payment Link</p>
                <p className="text-sm font-mono break-all">
                  https://pay.stelxpay.com/{showSuccess.data.link_token}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button
                  className="flex-1 btn-secondary"
                  onClick={() => copyToClipboard(`https://pay.stelxpay.com/${showSuccess.data.link_token}`)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
                <Button
                  className="flex-1 btn-primary"
                  onClick={() => handleShare(showSuccess.data)}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NavBar active="wallet" />
    </div>
  );
};

export default Receive;
